import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import { ArrowGlyph, Stage } from '@/components/agents/primitives';

import { imageUrl, type LandingImage } from './assets';
import { useInteriorLandingT, usePageVars, useStartInterior } from './kit';
import type { InteriorLandingPage } from './types';

/** Last call to action, with the page's own designs fanned out beside it. */
export default function FinalCta({ page }: { page: InteriorLandingPage }) {
  const { t } = useInteriorLandingT();
  const vars = usePageVars(page);
  const start = useStartInterior(page);

  const pictures: LandingImage[] = page.scenes
    .flatMap((scene) => scene.looks.map((look) => look.image))
    .slice(0, 3);

  return (
    <section className='mx-auto max-w-7xl px-3 pb-24 pt-8 sm:px-6 sm:pb-32 lg:px-8'>
      <Stage
        variant='indigo'
        halftoneAt='100% 100%'
        className='px-6 py-16 sm:px-14 sm:py-20'
      >
        <div className='relative grid items-center gap-14 lg:grid-cols-[1.4fr_1fr]'>
          <div>
            <h2
              className={clsxm(
                styles.display,
                'text-[2.25rem] font-semibold leading-[1.06] text-white sm:text-5xl lg:text-[3.5rem]',
              )}
            >
              {t(page.finalTitle, vars)}
            </h2>
            <p className='mt-6 max-w-lg text-lg leading-8 text-primary-100'>
              {t(
                'Upload a photo and get your first design free. No credit card needed.',
              )}
            </p>
            <div className='mt-10 flex flex-wrap items-center gap-3'>
              <button
                type='button'
                onClick={() => start({ surface: 'cta' })}
                className='inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-[15px] font-semibold text-primary-700 shadow-[0_10px_30px_-10px_rgba(20,10,80,0.6)] transition-colors duration-200 hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
              >
                {t('Start designing')}
                <ArrowGlyph />
              </button>
              <Link
                href='/pricing'
                className='inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-semibold text-white ring-1 ring-inset ring-white/35 transition duration-200 hover:bg-white/10 hover:ring-white/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
              >
                {t('See plans')}
              </Link>
            </div>
          </div>

          {pictures.length ? (
            <div aria-hidden='true' className='relative hidden h-72 lg:block'>
              {pictures.map((picture, index) => (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  key={picture.path}
                  src={imageUrl(picture, 640)}
                  alt=''
                  loading='lazy'
                  decoding='async'
                  className={clsxm(
                    'absolute w-[62%] rounded-2xl object-cover shadow-[0_30px_60px_-24px_rgba(10,5,50,0.75)] ring-4 ring-white/90',
                    index === 0 && 'end-0 top-0 aspect-[4/3] rotate-[4deg]',
                    index === 1 && 'start-0 top-10 aspect-[4/3] -rotate-[5deg]',
                    index === 2 &&
                      'bottom-0 end-[14%] aspect-[4/3] rotate-[1deg]',
                  )}
                />
              ))}
            </div>
          ) : null}
        </div>
      </Stage>
    </section>
  );
}
